import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { runtimeDataPlaneService } from '../../services/runtimeDataPlaneService';
import { enterpriseEventsService } from '../../services/enterpriseEventsService';
import { program6Service } from '../../services/program6Service';
import { program7Service } from '../../services/program7Service';
import { screenIdByPath, screenByPath } from './platformOpsNav';
import { EnterpriseModernizationActions } from './EnterpriseModernizationActions';

const ENTERPRISE_EVENT_LOADERS: Record<string, () => Promise<{ data?: unknown }>> = {
  'event-platform': () => enterpriseEventsService.getEventPlatform(),
  'outbox-framework': () => enterpriseEventsService.getOutbox(),
  'domain-migration': () => enterpriseEventsService.getDomains(),
  'read-model-projectors': () => enterpriseEventsService.getProjectors(),
  'db-optimization-registry': () => enterpriseEventsService.getDbOptimization(),
  'deployment-drain': () => enterpriseEventsService.getDeploymentDrain(),
  'program6-dashboard': () => program6Service.getStatus(),
  'program6-migrations': () => program6Service.getMigrations(),
  'program6-performance': () => program6Service.getStatus(),
  'program6-physical-optimization': () => program6Service.getPhysicalOptimizationStatus(),
  'program6-certification': () => program6Service.getCertificationStatus(),
  'program7-dashboard': () => program7Service.getStatus(),
  'program7-deployment': () => program7Service.getDeploymentStatus(),
  'program7-eoat': () => program7Service.getStatus(),
  'program7-certification': () => program7Service.getStatus(),
  'program7-readiness': () => program7Service.getReadinessReport(),
};

export const PlatformOpsScreenPage: React.FC = () => {
  const location = useLocation();
  const screen = screenByPath(location.pathname);
  const screenId = screenIdByPath(location.pathname);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<Record<string, unknown> | null>(null);
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const enterpriseLoader = ENTERPRISE_EVENT_LOADERS[screenId];
      if (enterpriseLoader) {
        const [screenResp, statusResp] = await Promise.all([
          enterpriseLoader(),
          enterpriseEventsService.getStatus(),
        ]);
        setPayload({ data: screenResp.data });
        setStatus((statusResp.data as Record<string, unknown>) || null);
      } else {
        const [screenResp, statusResp] = await Promise.all([
          runtimeDataPlaneService.getScreen(screenId),
          runtimeDataPlaneService.getStatus(),
        ]);
        setPayload((screenResp.data as Record<string, unknown>) || null);
        setStatus((statusResp.data as Record<string, unknown>) || null);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load screen');
    } finally {
      setLoading(false);
    }
  }, [screenId]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 30000);
    return () => window.clearInterval(id);
  }, [load]);

  const inner = (payload?.data as Record<string, unknown>) || payload;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
        <div>
          <h2 className="text-lg font-semibold text-enterprise-900">{screen?.label ?? 'Platform Operations'}</h2>
          <p className="mt-1 text-sm text-enterprise-600">{screen?.description}</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-lg border border-enterprise-200 px-3 py-2 text-sm text-enterprise-700 hover:bg-enterprise-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
        {ENTERPRISE_EVENT_LOADERS[screenId]
          ? screenId.startsWith('program7')
            ? 'Program 7 — final operational certification. Enable PROGRAM7_* flags in staging. No new architecture — validation only.'
            : screenId.startsWith('program6')
            ? 'Program 6 — first production data path changes. Statistics pilot in staging. Measurable improvement required.'
            : 'Modernization layer — all ENTERPRISE_* flags default OFF. Legacy business logic unchanged.'
          : 'Observability mode — enable RUNTIME_DATA_PLANE_ENABLED for full live probes.'}
      </div>

      {(ENTERPRISE_EVENT_LOADERS[screenId] || screenId.startsWith('program6') || screenId.startsWith('program7')) && (
        <EnterpriseModernizationActions
          screenId={screenId}
          onActionComplete={() => void load()}
          migrationPayload={
            screenId.startsWith('program6') && inner && typeof inner === 'object'
              ? (inner as Record<string, unknown>)
              : null
          }
        />
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      {screenId === 'dependency-graph' && inner && typeof inner === 'object' && 'nodes' in inner && (
        <DependencyGraphPreview data={inner as { nodes: { id: string; label: string; type: string }[]; edges: { from: string; to: string; label?: string }[] }} />
      )}

      {screenId === 'runtime-dashboard' && inner && typeof inner === 'object' && 'summary' in inner && (
        <DashboardSummary data={inner as { summary: Record<string, unknown> }} />
      )}

      <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-enterprise-500">Live data</h3>
        {loading && !payload ? (
          <p className="text-sm text-enterprise-500">Loading…</p>
        ) : (
          <pre className="max-h-[32rem] overflow-auto rounded bg-enterprise-50 p-3 text-xs text-enterprise-800">
            {JSON.stringify(inner, null, 2)}
          </pre>
        )}
      </div>

      {status && (
        <div className="rounded-xl border border-enterprise-200 bg-white p-4 text-sm shadow-sm">
          <h3 className="font-semibold text-enterprise-900">Platform status</h3>
          <pre className="mt-2 overflow-auto rounded bg-enterprise-50 p-3 text-xs">{JSON.stringify(status, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

const DashboardSummary: React.FC<{ data: { summary: Record<string, unknown> } }> = ({ data }) => {
  const s = data.summary || {};
  return (
    <div className="grid gap-4 sm:grid-cols-4">
      {[
        ['Live workers', s.live_workers],
        ['Queues', s.live_queues],
        ['Queue depth', s.total_queue_depth],
        ['Redis OK', s.redis_ok ? 'Yes' : 'No'],
      ].map(([label, value]) => (
        <div key={String(label)} className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-enterprise-500">{label}</p>
          <p className="mt-2 text-2xl font-bold text-enterprise-900">{String(value ?? '—')}</p>
        </div>
      ))}
    </div>
  );
};

const DependencyGraphPreview: React.FC<{
  data: { nodes: { id: string; label: string; type: string }[]; edges: { from: string; to: string; label?: string }[] };
}> = ({ data }) => {
  const typeColor: Record<string, string> = {
    service: 'bg-blue-100 text-blue-800',
    queue: 'bg-amber-100 text-amber-800',
    worker: 'bg-emerald-100 text-emerald-800',
    table: 'bg-purple-100 text-purple-800',
    redis: 'bg-red-100 text-red-800',
    storage: 'bg-slate-100 text-slate-800',
  };
  return (
    <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 font-semibold text-enterprise-900">Dependency graph (preview)</h3>
      <div className="flex flex-wrap gap-2">
        {(data.nodes || []).slice(0, 24).map((n) => (
          <span
            key={n.id}
            className={`rounded-full px-2 py-1 text-xs ${typeColor[n.type] || 'bg-enterprise-100 text-enterprise-700'}`}
            title={n.id}
          >
            {n.label}
          </span>
        ))}
      </div>
      <p className="mt-3 text-xs text-enterprise-500">{(data.edges || []).length} edges — click-through detail in JSON below</p>
    </div>
  );
};
